/**
 * FinanceDashboard — consulta de rentabilidade (Financeiro).
 *
 * Acesso só leitura. Responde a "A empresa está a vender com margem
 * suficiente? Há scrap fora do controlo? Qual a exposição a alumínio?"
 *
 * Layout:
 *   ▸ 4 KPIs: margem média do pipeline, margem média das ganhas, receita
 *     anual ganha, share VM (% do PV em matéria-prima).
 *   ▸ 3 charts: margem média ao longo do tempo (linha), composição média
 *     do custo (donut), margem por tipo de cliente (barras).
 *   ▸ 1 tabela: RFQs activas com PnL audit SUBPRECIFICADO ou CRITICO.
 */
import { Link } from 'react-router-dom';
import {
  KpiGrid, DonutChartCard, TimeSeriesCard, OperationalTable, VerdictBadge,
} from '@/components/dashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from 'recharts';
import { formatNumber } from '@/lib/utils';
import { t } from '@/i18n/translations';

export default function FinanceDashboard({ data, lang }) {
  const charts = data.charts || {};
  const tables = data.tables || {};

  const marginByClient = (charts.margin_by_client_type || []).map((d) => ({
    name: t(`client_type_${d.key}`, lang),
    margin: d.margin_pct == null ? 0 : d.margin_pct,
    count: d.count,
  }));

  return (
    <div className="space-y-6">
      <KpiGrid kpis={data.kpis || []} lang={lang} />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <TimeSeriesCard
            title={t('chart_margin_timeseries', lang)}
            data={(charts.margin_timeseries || []).map((d) => ({
              label: d.label,
              margin: d.margin_pct,
            }))}
            series={[
              { key: 'margin', labelKey: 'series_margin', color: '#3B82F6' },
            ]}
            kind="line"
            lang={lang}
          />
        </div>
        <DonutChartCard
          title={t('chart_cost_composition', lang)}
          data={(charts.cost_composition || []).map((d) => ({
            key: `cost_${d.key}`, value: d.value,
          }))}
          lang={lang}
          valueLabel="cost_total"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('chart_margin_by_client_type', lang)}</CardTitle>
          </CardHeader>
          <CardContent>
            {marginByClient.every((d) => !d.margin && !d.count) ? (
              <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
                {t('no_data', lang)}
              </div>
            ) : (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={marginByClient}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                           tickFormatter={(v) => `${v}%`} />
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                      formatter={(v) => `${formatNumber(v, lang, 1)}%`}
                    />
                    <Bar dataKey="margin" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <OperationalTable
          title={t('table_underpriced', lang)}
          items={tables.underpriced || []}
          lang={lang}
          emptyKey="no_underpriced"
          columns={[
            { key: 'reference_number', labelKey: 'rfq_ref',
              render: (r) => (
                <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                  {r.reference_number}
                </Link>
              ),
            },
            { key: 'verdict', labelKey: 'pnl_verdict',
              render: (r) => <VerdictBadge verdict={r.verdict} lang={lang} />,
            },
            { key: 'ebitda_implied_pct', labelKey: 'ebitda_implied_pct_short', align: 'right',
              render: (r) => (
                <span className="tnum">
                  {r.ebitda_implied_pct == null ? '—' : `${formatNumber(r.ebitda_implied_pct * 100, lang, 1)}%`}
                </span>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
